import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : wiki components
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Menu, Segment, List, Progress, Tab, Button } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { Sel, Actions } from 'uix/registries';
import { get, mapItems, items, dict, size, uniq, isFunction, isString, isNil } from 'ldash';
import { useSelector, useState, useEffect, useApi, useActions, usePartContext, useMemo, useRouter, useResettableState, useDebounce, usePersistentWithQueryOverride, useGraphics, usePhysicalNode } from 'uix/hooks';
import { hashString, formatDateTime, loadLazy } from 'uix/utils';
import { Toolbar, XMenu, TranslatedTable, ErrorBoundary, PortalTarget, AutoPortal, VIcon, XPopup, Link } from 'uix/components';
import { Editor } from 'uix/components/editor';
import { SingleFileUploader } from 'uix/upload/components';
import { TypeCaption } from 'uix/types/components';
// ---Local imports
import { MIME, MIMETYPES } from '../utils';
import { useCompletion, useWikiEditing, useWikiPreview } from '../hooks';
export { WikiModalTarget, WikiModalPortal } from './modal';
export { RevisionTable } from './revisions';
export { FulltextRenderPane, FulltextPreviewPane } from './fulltext';
export const ChangeHistory = loadLazy(()=>import(/* webpackChunkName: 'wiki' */ './history'));
//~ export {ChangeHistory} from './history'
// ---Globals
export const KeyMap = ({ keymap })=>{
    let combined = dict([
        ...items(keymap.pcDefault),
        ...items(keymap.sublime)
    ]);
    let divs = mapItems(combined, (shortcut, command)=>/*#__PURE__*/ _jsxs(List.Item, {
            children: [
                /*#__PURE__*/ _jsx(List.Content, {
                    floated: "right",
                    children: /*#__PURE__*/ _jsx("tt", {
                        children: command
                    })
                }),
                shortcut
            ]
        }, shortcut), (shortcut)=>shortcut !== 'fallthrough');
    return /*#__PURE__*/ _jsx(Segment, {
        attached: true,
        style: {
            height: '40vh',
            overflow: 'auto'
        },
        children: /*#__PURE__*/ _jsx(List, {
            divided: true,
            children: divs
        })
    });
};
export const BasicWikiEditor = ({ otid: external_otid, showMenu = false, readOnly = false, autoComplete = false, lineWrapping = false, preview = false, previewStoreKey = null, prettyPrint = false, caption = false, debounceId = 0 })=>{
    if (debounceId) {
        let [debounced_otid] = useDebounce(external_otid, debounceId, {
            leading: false
        });
        external_otid = debounced_otid;
    }
    let { wikitext, setWikitext, setMimetype, saveWikiState, revertWikiState, saveEnabled, otid, datatype, mimetype, caption: nodeCaption } = useWikiEditing({
        otid: external_otid
    });
    showMenu = showMenu && saveWikiState !== null;
    let callApi = useActions(Actions.callApi);
    let completion = useCompletion({
        mimetype
    });
    let codeChange = ({ value })=>{
        setWikitext(value);
    };
    useWikiPreview({
        text: wikitext,
        otid,
        datatype,
        mimetype,
        active: !!preview && saveEnabled,
        storeKey: previewStoreKey
    });
    let [editorCursor, setEditorCursor] = useState({
        line: 0,
        ch: 0
    });
    let cursorChange = ({ editor, data })=>{
        setEditorCursor(data);
    };
    let opts = {
        readOnly,
        lineWrapping
    };
    let doPrettyPrint = ()=>{
        callApi('prettyPrint', {
            text: wikitext,
            mimetype
        }, ({ prettyData })=>codeChange({
                value: prettyData
            }), {
            method: 'post'
        });
    };
    let extraKeys = {};
    if (!readOnly) {
        extraKeys = {
            ...extraKeys,
            'Ctrl-S': saveWikiState
        };
    }
    if (autoComplete) {
        extraKeys = {
            ...extraKeys,
            ...completion.extraKeys
        };
        opts = {
            ...opts,
            hintOptions: completion.hintOptions
        };
    }
    let menu = null;
    if (showMenu) {
        menu = /*#__PURE__*/ _jsxs(Toolbar, {
            children: [
                caption && /*#__PURE__*/ _jsx(XMenu.Label, {
                    content: nodeCaption || otid
                }),
                /*#__PURE__*/ _jsx(XMenu.BeginRight, {}),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: "save",
                    title: "Save (Strg+S)",
                    disabled: !saveEnabled,
                    onClick: saveWikiState
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: "undo",
                    title: "Undo",
                    disabled: !saveEnabled,
                    onClick: revertWikiState
                }),
                /*#__PURE__*/ _jsx(WikiMimetype, {
                    otid: otid,
                    mimetype: mimetype,
                    datatype: datatype,
                    setMimetype: setMimetype
                }),
                prettyPrint && /*#__PURE__*/ _jsx(XMenu.Item, {
                    disabled: mimetype == MIME.MARKDOWN,
                    onClick: doPrettyPrint,
                    icon: "prettyprint",
                    title: "Pretty Print"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            menu,
            /*#__PURE__*/ _jsx(Editor, {
                flex: true,
                mimetype: mimetype,
                text: wikitext,
                opts: opts,
                extraKeys: extraKeys,
                onBeforeChange: codeChange,
                onCursor: cursorChange,
                cursor: editorCursor
            })
        ]
    });
};
const ALLOWED_MIME = {
    1: [
        MIME.MARKDOWN,
        MIME.HTML
    ],
    2: [
        MIME.MARKDOWN,
        MIME.HTML,
        MIME.XML
    ],
    3: [
        MIME.MARKDOWN,
        MIME.HOCR
    ],
    21: [
        MIME.MARKDOWN,
        MIME.HTML,
        MIME.XHTML
    ],
    23: [
        MIME.MARKDOWN,
        MIME.HTML,
        MIME.XHTML
    ]
};
export const WikiMimetype = ({ otid, datatype, mimetype, setMimetype, icon })=>{
    let { reload } = usePartContext();
    let callApi = useActions('callApi');
    let onSetValue = (mimetype)=>{
        if (setMimetype) {
            setMimetype(mimetype);
        } else {
            callApi(`/api/updateWikiMimetype`, {
                otid,
                mimetype
            }, reload);
        }
    };
    let options = mapItems(MIMETYPES, (mt, text)=>({
            value: mt,
            text
        }), (mt)=>uniq([
            mimetype,
            ...ALLOWED_MIME[datatype] ?? []
        ]).includes(mt));
    return /*#__PURE__*/ _jsx(XMenu.Dropdown, {
        direction: "left",
        icon: icon,
        title: "Change Mimetype",
        onSetValue: onSetValue,
        value: mimetype,
        options: options
    });
};
export const ChildResourceUpload = ({ otid, className, allowImages, allowAudioVideo, saveApi = 'wiki/insertChildResource', deleteApi = 'wiki/deleteChildResource' })=>{
    /* manage child resources for otid
   */ let apiurl = useSelector(Sel.apiurl);
    let graphics = useGraphics([
        'types'
    ]);
    let callApi = useActions(Actions.callApi);
    let { data, reload } = useApi('wiki/getChildResources', {
        otid
    }, {
        condition: !!otid
    });
    let mimetypes = [];
    if (allowImages) {
        mimetypes = [
            ...mimetypes,
            ...[
                'image/png',
                'image/jpeg',
                'image/webp'
            ]
        ];
    }
    if (allowAudioVideo) {
        mimetypes = [
            ...mimetypes,
            ...[
                'video/mp4',
                'audio/mpeg',
                'image/webp'
            ]
        ];
    }
    mimetypes = mimetypes.join(',');
    let [uploadResult, setUploadResult] = useResettableState(null, [
        otid
    ]);
    let [progress, setProgress] = useState(null);
    useEffect(()=>{
        if (uploadResult?.state == 'upload done') {
            if (isFunction(saveApi)) {
                saveApi({
                    otid,
                    uploadid: uploadResult.uploadid,
                    reloadComponent: reload
                });
            } else {
                callApi(saveApi, {
                    parentid: otid,
                    uploadid: uploadResult.uploadid
                }, reload);
            }
        }
    }, [
        uploadResult
    ]);
    let resources = data?.resources || [];
    let webresources = resources.map((r)=>{
        let node = /*#__PURE__*/ _jsx(TypeCaption, {
            ...r,
            caption: r.caption || r.fname,
            style: {
                paddingLeft: '5px'
            }
        });
        // Popup needs another span to open?
        node = /*#__PURE__*/ _jsxs("span", {
            style: {
                display: 'inline-flex',
                cursor: 'pointer'
            },
            children: [
                node,
                /*#__PURE__*/ _jsx(Button, {
                    title: "Delete",
                    size: "mini",
                    secondary: true,
                    style: {
                        color: 'var(--vl_body_color)'
                    },
                    icon: "trash",
                    onClick: ()=>callApi(deleteApi, {
                            resource_otid: r.otid
                        }, reload)
                })
            ]
        }, r.otid);
        let popup = null;
        let src = apiurl.href(`/download/webresource/${r.otid}`);
        if (r.subtype == 2) {
            popup = /*#__PURE__*/ _jsx("img", {
                style: {
                    width: '10vw'
                },
                src: src
            });
        } else if (r.mimetype == 'video/mp4') {
            popup = /*#__PURE__*/ _jsx("video", {
                controls: true,
                width: "300",
                children: /*#__PURE__*/ _jsx("source", {
                    src: src,
                    type: "video/mp4"
                })
            });
        } else if (r.mimetype == 'audio/mpeg') {
            popup = /*#__PURE__*/ _jsx("audio", {
                controls: true,
                children: /*#__PURE__*/ _jsx("source", {
                    src: src,
                    type: "audio/mpeg"
                })
            });
        }
        return popup ? /*#__PURE__*/ _jsx(XPopup, {
            trigger: node,
            hoverable: true,
            children: popup
        }, r.otid) : node;
    });
    if (!graphics.types) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: className,
        children: [
            webresources,
            ' ',
            /*#__PURE__*/ _jsx(SingleFileUploader, {
                onUploadDone: setUploadResult,
                onUploadProgress: setProgress,
                as: "span",
                mimetypes: mimetypes,
                children: /*#__PURE__*/ _jsxs("span", {
                    children: [
                        /*#__PURE__*/ _jsx(VIcon, {
                            name: "ws_add-file"
                        }),
                        "Upload"
                    ]
                })
            }),
            progress?.active && /*#__PURE__*/ _jsx(Progress, {
                percent: progress.percentage,
                attached: "bottom",
                size: "tiny"
            })
        ]
    });
};
export const WikiAlternatives = ({})=>{
    let { datatype, ...r } = usePhysicalNode();
    let { request, go } = useRouter();
    let dcfg = useSelector(Sel.dconfig);
    if (datatype == 10) {
        let activeIndex = null;
        let panes = dcfg.domain.wikipages.map((ident, i)=>{
            if (request.id == ident) {
                activeIndex = i;
            }
            return {
                menuItem: /*#__PURE__*/ _jsx(Menu.Item, {
                    as: "span",
                    children: /*#__PURE__*/ _jsx(Link, {
                        id: ident,
                        children: ident
                    })
                }, ident),
                wiki_ident: ident
            };
        });
        return /*#__PURE__*/ _jsx(Tab, {
            menu: {
                attached: 'bottom',
                style: {
                    width: '99.99%',
                    borderRight: '0px'
                }
            },
            panes: panes,
            activeIndex: activeIndex
        });
    }
    return null;
};
export const DebugButton = loadLazy(()=>import(/* webpackChunkName: 'wiki' */ './debugdlg'));
export const WikiPreview = loadLazy(()=>import(/* webpackChunkName: 'wiki' */ './preview'));
